import { IconX } from '@developerz/ui';
import type { JSX } from 'solid-js';
import { t } from '../../i18n';

// One pane's own title bar (#4397, epic #4391) — Terminator's per-pane bar,
// translated into house tokens: a stronger hairline plus the live dot when
// FOCUSED and STREAMING (step 2), never the accent as decoration. The window
// keeps its ONE `<h1>` in `TerminalTitlebar.tsx`; this is a `<div>` toolbar,
// not a second heading — four panes must never produce four `<h1>`s.
//
// THE MENU is a plain button row (`role="toolbar"`), not a dropdown: four
// always-visible actions (split beside, split below, zoom, close) fit this
// bar's width and a full ARIA menu widget (roving tabindex, Escape-to-close,
// arrow-key navigation) would be real complexity bought for nothing a
// dropdown does better here. "The pane title-bar menu" (the issue's own
// words) is satisfied by a toolbar of actions living IN the title bar.
export function TerminalPaneTitlebar(props: {
  readonly label: string;
  readonly focused: boolean;
  readonly live: boolean;
  readonly zoomed: boolean;
  /** #4399 slice 08 — this pane's own verbose toggle. See `TerminalPane.tsx`. */
  readonly verbose: boolean;
  readonly canClose: boolean;
  readonly onFocus: () => void;
  readonly onSplitRow: () => void;
  readonly onSplitColumn: () => void;
  readonly onToggleZoom: () => void;
  readonly onToggleVerbose: () => void;
  readonly onClose: () => void;
  /** Move focus to the terminal command line (#4398) — the button is a
   *  discoverable equivalent of the global `:` keymap binding, not a second
   *  input: there is exactly one command line for the whole window. */
  readonly onOpenCommandLine: () => void;
  /** Open this pane's search (#4401) — the menu equivalent of Ctrl/⌘+F. */
  readonly onOpenSearch: () => void;
}): JSX.Element {
  return (
    <div
      class="terminal__pane-titlebar"
      classList={{ 'terminal__pane-titlebar--focused': props.focused }}
      onFocusIn={props.onFocus}
      onPointerDown={props.onFocus}
    >
      <span class="terminal__pane-path">
        {/* The live dot marks a genuinely open, focused stream — never mere
            decoration (`scripts/accent-discipline.test.ts`: a live-named
            selector, applied only while both conditions hold). */}
        <span
          class="terminal__pane-dot"
          classList={{ 'terminal__pane-dot--live': props.live && props.focused }}
          aria-hidden="true"
        />
        {props.label}
      </span>
      <div class="terminal__pane-toolbar" role="toolbar" aria-label={t('terminal.pane_toolbar')}>
        <button type="button" class="terminal__pane-action" onClick={props.onSplitRow}>
          {t('terminal.pane_split_row')}
        </button>
        <button type="button" class="terminal__pane-action" onClick={props.onSplitColumn}>
          {t('terminal.pane_split_column')}
        </button>
        <button
          type="button"
          class="terminal__pane-action"
          aria-pressed={props.zoomed}
          onClick={props.onToggleZoom}
        >
          {props.zoomed ? t('terminal.pane_restore') : t('terminal.pane_zoom')}
        </button>
        <button
          type="button"
          class="terminal__pane-action"
          aria-pressed={props.verbose}
          aria-label={props.verbose ? t('terminal.verbose_on') : t('terminal.verbose_off')}
          onClick={props.onToggleVerbose}
        >
          {t('terminal.verbose_label')}
        </button>
        <button type="button" class="terminal__pane-action" onClick={props.onOpenSearch}>
          {t('terminal.pane_open_search')}
        </button>
        <button type="button" class="terminal__pane-action" onClick={props.onOpenCommandLine}>
          {t('terminal.pane_open_command_line')}
        </button>
        <button
          type="button"
          class="terminal__pane-action"
          disabled={!props.canClose}
          aria-label={t('terminal.pane_close')}
          title={t('terminal.pane_close')}
          onClick={props.onClose}
        >
          <IconX size="0.75rem" />
        </button>
      </div>
    </div>
  );
}
